import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { scrollState } from '@/lib/depth';

/**
 * Two particle worlds in one scene:
 *  — cool white particles drifting upward (creator world)
 *  — warm ash/ember particles rising from below (fantasy world)
 * Cross-faded by scrollState.blend. Particles rush past on scroll (z drift).
 */
export const ParticleField = ({ dense = true }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: 'low-power' });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const fog = new THREE.FogExp2(0x000000, 0.016);
    scene.fog = fog;
    const camera = new THREE.PerspectiveCamera(62, window.innerWidth / window.innerHeight, 0.1, 240);
    camera.position.set(0, 0, 34);

    // The hero/diorama imagery now lives in the DOM (DioramaZoom); this scene is
    // just the atmospheric particle worlds layered over it.
    const COOL_COUNT = dense ? 780 : 340;
    const EMBER_COUNT = dense ? 520 : 240;

    // — cool world —
    const coolPos = new Float32Array(COOL_COUNT * 3);
    const coolVel = new Float32Array(COOL_COUNT);
    for (let i = 0; i < COOL_COUNT; i += 1) {
      coolPos[i * 3] = (Math.random() - 0.5) * 95;
      coolPos[i * 3 + 1] = (Math.random() - 0.5) * 64;
      coolPos[i * 3 + 2] = Math.random() * 95 - 65;
      coolVel[i] = 0.008 + Math.random() * 0.03;
    }
    const coolGeo = new THREE.BufferGeometry();
    coolGeo.setAttribute('position', new THREE.BufferAttribute(coolPos, 3));
    const coolMat = new THREE.PointsMaterial({
      color: 0xe8ecf2,
      size: 0.18,
      transparent: true,
      opacity: 0.62,
      depthWrite: false,
      sizeAttenuation: true,
    });
    scene.add(new THREE.Points(coolGeo, coolMat));

    // — ember world —
    const emberPos = new Float32Array(EMBER_COUNT * 3);
    const emberVel = new Float32Array(EMBER_COUNT);
    const emberSeed = new Float32Array(EMBER_COUNT);
    const emberCol = new Float32Array(EMBER_COUNT * 3);
    const warm = new THREE.Color(0xff7a3a);
    const deep = new THREE.Color(0x8b1a1a);
    for (let i = 0; i < EMBER_COUNT; i += 1) {
      emberPos[i * 3] = (Math.random() - 0.5) * 85;
      emberPos[i * 3 + 1] = -34 + Math.random() * 56;
      emberPos[i * 3 + 2] = Math.random() * 85 - 60;
      emberVel[i] = 0.02 + Math.random() * 0.055;
      emberSeed[i] = Math.random() * Math.PI * 2;
      const c = warm.clone().lerp(deep, Math.random());
      emberCol[i * 3] = c.r;
      emberCol[i * 3 + 1] = c.g;
      emberCol[i * 3 + 2] = c.b;
    }
    const emberGeo = new THREE.BufferGeometry();
    emberGeo.setAttribute('position', new THREE.BufferAttribute(emberPos, 3));
    emberGeo.setAttribute('color', new THREE.BufferAttribute(emberCol, 3));
    const emberMat = new THREE.PointsMaterial({
      vertexColors: true,
      size: 0.26,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    scene.add(new THREE.Points(emberGeo, emberMat));

    const fogCool = new THREE.Color(0x04060c); // deep moonlit night of the cliff world
    const fogWarm = new THREE.Color(0x190606); // ember depths below the threshold
    let lastCam = scrollState.camera;
    let t = 0;
    let raf;

    const tick = () => {
      t += 0.016;
      const { blend } = scrollState;
      const cam = scrollState.camera;
      const dz = (cam - lastCam) * 0.018;
      lastCam = cam;

      fog.density = 0.016;

      for (let i = 0; i < COOL_COUNT; i += 1) {
        coolPos[i * 3 + 1] += coolVel[i];
        if (coolPos[i * 3 + 1] > 34) coolPos[i * 3 + 1] = -34;
        coolPos[i * 3 + 2] += dz;
        if (coolPos[i * 3 + 2] > 36) coolPos[i * 3 + 2] -= 95;
        else if (coolPos[i * 3 + 2] < -66) coolPos[i * 3 + 2] += 95;
      }
      coolGeo.attributes.position.needsUpdate = true;

      for (let i = 0; i < EMBER_COUNT; i += 1) {
        emberPos[i * 3 + 1] += emberVel[i] * (0.6 + blend);
        emberPos[i * 3] += Math.sin(t * 0.8 + emberSeed[i]) * 0.012;
        if (emberPos[i * 3 + 1] > 32) emberPos[i * 3 + 1] = -34;
        emberPos[i * 3 + 2] += dz;
        if (emberPos[i * 3 + 2] > 32) emberPos[i * 3 + 2] -= 85;
        else if (emberPos[i * 3 + 2] < -62) emberPos[i * 3 + 2] += 85;
      }
      emberGeo.attributes.position.needsUpdate = true;

      coolMat.opacity = 0.62 * (1 - blend);
      emberMat.opacity = 0.95 * blend;
      // night cliff world -> ember depths below the threshold
      fog.color.copy(fogCool).lerp(fogWarm, blend);
      camera.rotation.z = Math.sin(t * 0.05) * 0.008;

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      coolGeo.dispose();
      coolMat.dispose();
      emberGeo.dispose();
      emberMat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [dense]);

  return <div ref={mountRef} className="fixed inset-0 z-[3] pointer-events-none" aria-hidden="true" data-testid="particle-field" />;
};
